import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Image, Linking } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { PublicNav } from "../../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";

const AKC_FASTCAT_URL = "https://www.akc.org/sports/coursing/fast-cat/";
const APP_STORE_URL = "https://apps.apple.com/us/app/dash-hound/id6762554245";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.dashhound.app";

// The scoring math, spelled out. Values mirror the app's calculations
// (lib/calculations.ts) and AKC's official Fast CAT handicap system.
export default function PostCalculateFastCatPoints() {
  const router = useRouter();

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>How to Calculate Fast CAT Points — Dash-Hound</title>
          <meta name="description" content="Fast CAT points explained: convert your dog's 100-yard time to MPH (204.545 ÷ seconds), apply the AKC height handicap (1.0 / 1.5 / 2.0), and multiply to get points. Worked examples plus a free calculator." />
          <meta property="og:title" content="How to Calculate Fast CAT Points — Dash-Hound" />
          <meta property="og:description" content="The exact Fast CAT scoring formula — MPH × height handicap — with worked examples and a free points calculator." />
          <meta property="og:image" content="https://dashhound.app/blog/phil-teton-fast-cat.jpg" />
          <meta property="og:url" content="https://dashhound.app/blog/how-to-calculate-fast-cat-points" />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="How to Calculate Fast CAT Points — Dash-Hound" />
          <meta name="twitter:description" content="The exact Fast CAT scoring formula — MPH × height handicap — with worked examples and a free points calculator." />
          <meta name="twitter:image" content="https://dashhound.app/blog/phil-teton-fast-cat.jpg" />
        </Head>
      )}

      <PublicNav />

      <View style={styles.subBar}>
        <TouchableOpacity onPress={() => router.push("/(auth)/blog" as any)}>
          <Text style={styles.subBarBack}>← Back to Blog</Text>
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroCategory}>SCORING</Text>
          <Text style={styles.heroTitle}>How to Calculate Fast CAT Points</Text>
          <Text style={styles.heroMeta}>August 11, 2026 · 6 min read</Text>
        </View>

        <View style={styles.body}>
          <Text style={styles.lead}>
            Your dog rips down the 100-yard straightaway, and a few days later a number shows up next to their name on the AKC site. Where does that number come from? Fast CAT points aren't magic — they come from two things: how fast your dog ran, and how tall your dog is. Here's the exact math, so you can figure out your dog's points before AKC ever posts them.
          </Text>

          <Figure
            src="/blog/phil-teton-fast-cat.jpg"
            alt="Phil releasing Teton, a blue heeler, at the Fast CAT start line"
            caption="Phil sending Teton down the 100-yard dash. Everything below starts with one number: Teton's time from this line to the finish."
          />

          <H2>The whole formula</H2>
          <Text style={styles.p}>
            Every Fast CAT run comes down to a single equation:
          </Text>
          <View style={styles.formula}>
            <Text style={styles.formulaText}>Points = MPH × Handicap</Text>
          </View>
          <Text style={styles.p}>
            That's it. Two inputs — your dog's speed and a height-based multiplier — and everything else falls out of them. Let's build both.
          </Text>

          <H2>Step 1: Turn the time into MPH</H2>
          <Text style={styles.p}>
            Fast CAT is a timed 100-yard dash. The judge records a time in seconds; the app (and AKC) convert that to miles per hour. The conversion constant is <Strong>204.545</Strong>:
          </Text>
          <View style={styles.formula}>
            <Text style={styles.formulaText}>MPH = 204.545 ÷ time (seconds)</Text>
          </View>
          <Text style={styles.p}>
            Where does 204.545 come from? A mile is 1,760 yards and an hour is 3,600 seconds, so covering 100 yards in one second works out to 100 × 3,600 ÷ 1,760 = 204.545 mph. Divide that by your dog's actual seconds and you have their speed.
          </Text>
          <Text style={styles.p}>
            <Strong>Example:</Strong> say Teton (above) runs the 100 yards in 7.80 seconds. 204.545 ÷ 7.80 = <Strong>26.22 MPH</Strong>.
          </Text>

          <H2>Step 2: Apply the height handicap</H2>
          <Text style={styles.p}>
            Here's the part that surprises newcomers: a shorter dog earns more points for the same speed. AKC multiplies each dog's MPH by a handicap based on its height at the withers (the shoulders), so smaller dogs aren't buried under the greyhounds. There are three brackets:
          </Text>
          <Bullet><Strong>18″ and over:</Strong> × 1.0 (no handicap)</Bullet>
          <Bullet><Strong>12″ to under 18″:</Strong> × 1.5</Bullet>
          <Bullet><Strong>Under 12″:</Strong> × 2.0</Bullet>
          <Text style={styles.p}>
            Teton is an Australian Cattle Dog standing about 19″, so he lands in the top bracket — a × 1.0 handicap, meaning his points equal his raw speed.
          </Text>

          <H2>Step 3: Multiply</H2>
          <Text style={styles.p}>
            Now put the two together:
          </Text>
          <Bullet><Strong>Teton</Strong> (19″, ran 26.22 MPH): 26.22 × 1.0 = <Strong>26.22 points</Strong> for that run.</Bullet>
          <Bullet><Strong>A 14″ Pembroke Welsh Corgi</Strong> running a slower 20.00 MPH: 20.00 × 1.5 = <Strong>30.00 points</Strong>.</Bullet>
          <Text style={styles.p}>
            Notice what happened: the corgi ran 6 MPH slower than Teton but earned <Strong>more</Strong> points, because the handicap does exactly what it's designed to do. That's why the Fast CAT rankings are full of dogs you'd never expect to see near the top.
          </Text>

          <H2>Two runs a day, and how points add up</H2>
          <Text style={styles.p}>
            Most events offer two runs per day, and <Strong>every qualifying run earns points</Strong> that add to your dog's lifetime total. One thing to keep straight: the <Strong>points</Strong> that earn titles are the running total of every run, but a dog's <Strong>ranking speed</Strong> is a separate figure — the average of their three fastest runs. So you'll see two numbers for a dog: cumulative points (for titles) and best-3-average MPH (for the leaderboard). This article is about the points.
          </Text>

          <H2>How points become titles</H2>
          <Text style={styles.p}>
            Once the points pile up, AKC awards suffix titles at set thresholds:
          </Text>
          <Bullet><Strong>BCAT</Strong> — 150 points</Bullet>
          <Bullet><Strong>DCAT</Strong> — 500 points</Bullet>
          <Bullet><Strong>FCAT</Strong> — 1,000 points</Bullet>
          <Bullet><Strong>FCAT2, FCAT3, …</Strong> — every additional 500 points after FCAT (1,500, 2,000, and so on)</Bullet>
          <Text style={styles.p}>
            At Teton's ~26 points per run, two runs a weekend is about 52 points — so BCAT lands in roughly three event days. A smaller dog with a × 2.0 handicap gets there even faster.
          </Text>

          <H2>Skip the arithmetic</H2>
          <Text style={styles.p}>
            You don't have to do any of this by hand. We built a free{" "}
            <A onPress={() => router.push("/(auth)/tools" as any)}>Fast CAT points calculator</A>{" "}
            that converts a time to MPH and points, tells you how many runs you need to your next title, and even figures out what you'd need on a third run to hit a target average. And when you log a run in the Dash-Hound app, all of this is computed for you automatically and tracked event to event.
          </Text>
          <Text style={styles.p}>
            Want the official word? AKC publishes the full{" "}
            <A onPress={() => Linking.openURL(AKC_FASTCAT_URL)}>Fast CAT rules and regulations</A>{" "}
            on their site.
          </Text>

          <View style={styles.cta}>
            <Text style={styles.ctaTitle}>Track it automatically</Text>
            <Text style={styles.ctaText}>
              Log a run and Dash-Hound calculates the MPH, applies the handicap, adds the points, and tracks your dog's title progress and breed rank — no spreadsheet required.
            </Text>
            <View style={{ flexDirection: "row", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <TouchableOpacity style={styles.ctaPrimary} onPress={() => router.push("/(auth)/tools" as any)}>
                <Text style={styles.ctaPrimaryText}>Try the Calculator →</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ctaSecondary} onPress={() => Linking.openURL(APP_STORE_URL)}>
                <Text style={styles.ctaSecondaryText}> Download on iOS</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ctaSecondary} onPress={() => Linking.openURL(PLAY_STORE_URL)}>
                <Text style={styles.ctaSecondaryText}>▶ Get it on Android</Text>
              </TouchableOpacity>
            </View>
          </View>

          <Text style={styles.disclaimer}>
            Numbers here are estimates for understanding the system — official points are awarded on AKC's recorded times.
          </Text>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

function H2({ children }: { children: string }) {
  return <Text style={styles.h2}>{children}</Text>;
}
function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.bulletRow}>
      <Text style={styles.bulletDot}>•</Text>
      <Text style={styles.bulletText}>{children}</Text>
    </View>
  );
}
function Strong({ children }: { children: React.ReactNode }) {
  return <Text style={{ fontWeight: "700", color: NAVY }}>{children}</Text>;
}
function A({ children, onPress }: { children: React.ReactNode; onPress: () => void }) {
  return (
    <Text style={styles.link} onPress={onPress}>
      {children}
    </Text>
  );
}
function Figure({ src, alt, caption }: { src: string; alt: string; caption?: string }) {
  return (
    <View style={styles.figure}>
      <View style={styles.figureFrame}>
        {Platform.OS === "web" ? (
          // @ts-ignore — raw img on web for proper layout + sharp render
          <img src={src} alt={alt} style={{ width: "100%", height: "auto", display: "block" } as any} />
        ) : (
          <Image source={{ uri: `https://dashhound.app${src}` }} style={{ width: "100%", aspectRatio: 16 / 10 }} resizeMode="cover" />
        )}
      </View>
      {caption ? <Text style={styles.figureCaption}>{caption}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  subBar: {
    backgroundColor: "#FFF7E6",
    borderBottomWidth: 1,
    borderBottomColor: "#F5DEB3",
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  subBarBack: { color: "#92400E", fontSize: 14, fontWeight: "600" },
  content: { flexGrow: 1 },
  hero: {
    backgroundColor: "#FFF7E6",
    paddingHorizontal: 24,
    paddingTop: 32,
    paddingBottom: 40,
    alignItems: "center",
  },
  heroCategory: { color: "#92400E", fontSize: 12, fontWeight: "800", letterSpacing: 1, marginBottom: 8 },
  heroTitle: { color: NAVY, fontSize: 30, fontWeight: "900", textAlign: "center", maxWidth: 720, lineHeight: 38 },
  heroMeta: { color: "#92400E", fontSize: 13, marginTop: 12 },
  body: {
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 20,
    paddingVertical: 32,
  },
  lead: { fontSize: 17, color: TEXT, lineHeight: 28, marginBottom: 8, fontWeight: "500" },
  h2: { fontSize: 22, fontWeight: "800", color: NAVY, marginTop: 32, marginBottom: 12 },
  p: { fontSize: 16, color: TEXT, lineHeight: 26, marginBottom: 12 },
  link: { color: "#B45309", fontWeight: "700", textDecorationLine: "underline" },
  formula: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    paddingVertical: 18,
    paddingHorizontal: 16,
    alignItems: "center",
    marginVertical: 10,
  },
  formulaText: { fontSize: 20, fontWeight: "800", color: NAVY, letterSpacing: 0.3, textAlign: "center" },
  bulletRow: { flexDirection: "row", marginBottom: 8, paddingLeft: 4 },
  bulletDot: { fontSize: 16, color: ORANGE, marginRight: 10, lineHeight: 26, fontWeight: "700" },
  bulletText: { flex: 1, fontSize: 16, color: TEXT, lineHeight: 26 },
  figure: { marginVertical: 20 },
  figureFrame: { borderRadius: 14, overflow: "hidden", backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB" },
  figureCaption: { fontSize: 13, color: "#6B7280", marginTop: 8, fontStyle: "italic", textAlign: "center", lineHeight: 20 },
  cta: {
    marginTop: 40,
    padding: 22,
    backgroundColor: "#F9FAFB",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  ctaTitle: { fontSize: 20, fontWeight: "800", color: NAVY, marginBottom: 8 },
  ctaText: { fontSize: 15, color: TEXT, lineHeight: 22 },
  ctaPrimary: { backgroundColor: NAVY, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaPrimaryText: { color: WHITE, fontWeight: "700", fontSize: 14 },
  ctaSecondary: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaSecondaryText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  disclaimer: { fontSize: 13, color: "#6B7280", fontStyle: "italic", marginTop: 24, lineHeight: 20 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center" },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
